import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Search, Bell, HelpCircle, ChevronDown } from 'lucide-react';
import { getAllNotifications, markNotificationRead } from '../../store/actions';
import { linkedItemPath } from '../../constants/garmentCodes';
import { useToast } from '../ui/Toast';
import useLogout from '../../auth/useLogout';
import LogoutModal from '../customComponents/logoutModal/LogoutModal';
import { extractRoleFromUser, isBrandRole } from '../../utils/userRole';
import { CREATE_INTENT } from '../../customHooks/useCreateIntent';

const PREVIEW_LIMIT = 5;

function useHeaderNotifications(basePath) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const unread = useSelector((state) => state.notification?.unreadCount || 0);
  const notifications = useSelector((state) => state.notification?.notifications || []);
  const loading = useSelector((state) => state.notification?.loading);

  const load = () => dispatch(getAllNotifications());
  const open = (n) => {
    if (!n.read) dispatch(markNotificationRead({ id: n.id }));
    navigate(linkedItemPath(basePath, n.linkedItemType) || `${basePath}/notifications`);
  };
  return { unread, preview: notifications.slice(0, PREVIEW_LIMIT), loading, load, open };
}

function NotificationPreview({ items, loading, onOpen, unreadClass, hoverClass }) {
  if (loading && !items.length) return <p className="px-4 py-6 text-center text-[12px] text-gray-400">Loading…</p>;
  if (!items.length) return <p className="px-4 py-6 text-center text-[12px] text-gray-400">No notifications yet</p>;
  return items.map((n) => (
    <button
      key={n.id}
      onClick={() => onOpen(n)}
      className={`w-full text-left px-4 py-3 ${hoverClass} border-b border-[#F4F3F0] transition-colors ${!n.read ? unreadClass : ''}`}
    >
      <p className="text-[12px] text-[#1A1915] font-medium leading-snug">{n.title}</p>
      {n.body && <p className="text-[11px] text-[#5C5A54] leading-snug mt-0.5">{n.body}</p>}
    </button>
  ));
}

export function BrandHeader({ title, subtitle, actions }) {
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [notifOpen, setNotifOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);
  const { showToast } = useToast();
  const navigate = useNavigate();
  const handleLogout = useLogout();
  const notif = useHeaderNotifications('/brand');
  const unread = notif.unread;
  const loginUser = useSelector((state) => state.auth?.loginUser);
  const displayName = loginUser?.name || loginUser?.username || '';
  const displayEmail = loginUser?.email || '';
  const displayRole = extractRoleFromUser(loginUser);
  const avatarLetter = (displayName?.[0] || 'B').toUpperCase();

  return (
    <>
      <header className="app-header h-14 border-b border-[#E5E3DE] bg-white flex items-center gap-4 shrink-0 sticky top-0 z-30">
        {/* Title */}
        <div className="flex-1 min-w-0">
          <h1
            className="text-sm font-semibold text-[#1A1915] truncate"
            style={{ fontFamily: 'Manrope, Inter, sans-serif' }}
          >
            {title}
          </h1>
          {subtitle && <p className="text-xs text-[#9B9893] truncate">{subtitle}</p>}
        </div>

        {/* Search */}
        <div className="relative">
          {searchOpen ? (
            <div className="flex items-center">
              <Search size={14} className="absolute left-3 text-[#9B9893]" />
              <input
                autoFocus
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onBlur={() => {
                  if (!searchQuery) setSearchOpen(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') setSearchOpen(false);
                  if (e.key === 'Enter' && searchQuery.trim()) {
                    navigate(`/brand/orders?q=${encodeURIComponent(searchQuery)}`);
                  }
                }}
                placeholder="Search orders, requirements, transactions..."
                className="pl-9 pr-4 py-1.5 text-sm bg-[#F4F3F0] border border-[#E5E3DE] rounded-lg w-72 outline-none focus:border-[#3D52A0] focus:bg-white transition-colors"
              />
            </div>
          ) : (
            <button
              onClick={() => setSearchOpen(true)}
              className="p-2 rounded-lg hover:bg-[#F4F3F0] text-[#9B9893] hover:text-[#5C5A54] transition-colors"
              title="Search"
            >
              <Search size={16} />
            </button>
          )}
        </div>

        {/* Actions slot */}
        {actions}

        {/* Notifications */}
        <div className="relative">
          <button
            onClick={() => {
              if (!notifOpen) notif.load();
              setNotifOpen(!notifOpen);
              setUserOpen(false);
            }}
            className="relative p-2 rounded-lg hover:bg-[#F4F3F0] text-[#9B9893] hover:text-[#5C5A54] transition-colors"
            title="Notifications"
          >
            <Bell size={16} />
            {unread > 0 && (
              <span className="absolute top-1 right-1 w-4 h-4 bg-[#C0222B] rounded-full text-[9px] font-bold text-white flex items-center justify-center">
                {unread}
              </span>
            )}
          </button>
          {notifOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setNotifOpen(false)} />
              <div className="absolute right-0 top-full mt-1 w-80 bg-white border border-[#E5E3DE] rounded-xl shadow-xl z-50 overflow-hidden">
                <div className="px-4 py-3 border-b border-[#E5E3DE] flex items-center justify-between">
                  <span className="text-[13px] font-semibold text-[#1A1915]">Notifications</span>
                  <button
                    className="text-[11px] text-[#3D52A0] font-medium hover:underline"
                    onClick={() => {
                      navigate('/brand/notifications');
                      setNotifOpen(false);
                    }}
                  >
                    View all
                  </button>
                </div>
                <NotificationPreview
                  items={notif.preview}
                  loading={notif.loading}
                  unreadClass="bg-[#EEF0FB]/50"
                  hoverClass="hover:bg-[#F4F3F0]"
                  onOpen={(n) => {
                    notif.open(n);
                    setNotifOpen(false);
                  }}
                />
              </div>
            </>
          )}
        </div>

        {/* Help */}
        <button
          onClick={() => showToast && showToast('Help centre & documentation coming soon', 'info')}
          className="p-2 rounded-lg hover:bg-[#F4F3F0] text-[#9B9893] hover:text-[#5C5A54] transition-colors"
          title="Help & Support"
        >
          <HelpCircle size={16} />
        </button>

        {/* User avatar */}
        <div className="relative">
          <div
            onClick={() => {
              setUserOpen(!userOpen);
              setNotifOpen(false);
            }}
            className="flex items-center gap-2 cursor-pointer hover:bg-[#F4F3F0] rounded-lg px-2 py-1 transition-colors"
          >
            <div className="w-7 h-7 bg-[#1E2A3B] rounded-full flex items-center justify-center">
              <span className="text-[10px] font-bold text-white">{avatarLetter}</span>
            </div>
            <div className="hidden sm:block text-left">
              <p className="text-xs font-semibold text-[#1A1915] max-w-[140px] truncate" title={displayName}>{displayName}</p>
              <p className="text-[10px] text-[#9B9893] max-w-[140px] truncate">{displayRole}</p>
            </div>
            <ChevronDown size={12} className="text-[#9B9893]" />
          </div>

          {userOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setUserOpen(false)} />
              <div className="absolute right-0 top-full mt-1 w-52 bg-white border border-[#E5E3DE] rounded-xl shadow-xl z-50 py-1">
                <div className="px-4 py-3 border-b border-[#E5E3DE]">
                  <div className="text-[12px] font-semibold text-[#1A1915] truncate" title={displayName}>{displayName}</div>
                  <div className="text-[11px] text-[#9B9893] truncate" title={displayEmail}>{displayEmail}</div>
                  <div className="text-[10px] font-medium text-[#3D52A0] mt-1 bg-[#EEF0FB] inline-block px-1.5 py-0.5 rounded">
                    {displayRole}
                  </div>
                </div>
                <button
                  className="w-full text-left px-4 py-2 text-[12px] text-[#5C5A54] hover:bg-[#F4F3F0] hover:text-[#1A1915] transition-colors"
                  onClick={() => {
                    navigate('/brand/settings');
                    setUserOpen(false);
                  }}
                >
                  Brand Settings
                </button>
                <button
                  className="w-full text-left px-4 py-2 text-[12px] text-[#5C5A54] hover:bg-[#F4F3F0] hover:text-[#1A1915] transition-colors"
                  onClick={() => {
                    navigate('/brand/team');
                    setUserOpen(false);
                  }}
                >
                  Team Members
                </button>
                <button
                  className="w-full text-left px-4 py-2 text-[12px] text-[#5C5A54] hover:bg-[#F4F3F0] hover:text-[#1A1915] transition-colors"
                  onClick={() => {
                    showToast && showToast('Help centre & documentation coming soon', 'info');
                    setUserOpen(false);
                  }}
                >
                  Help & Support
                </button>
                <div className="border-t border-[#E5E3DE] mt-1 pt-1">
                  <button
                    className="w-full text-left px-4 py-2 text-[12px] text-[#C0222B] hover:bg-[#FDECEA] transition-colors"
                    onClick={() => {
                      setUserOpen(false);
                      setLogoutModalOpen(true);
                    }}
                  >
                    Logout
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </header>

      <LogoutModal
        isOpen={logoutModalOpen}
        onClose={() => setLogoutModalOpen(false)}
        onConfirm={() => {
          setLogoutModalOpen(false);
          handleLogout();
        }}
      />
    </>
  );
}

export function ManufacturerHeader({ title, breadcrumbs }) {
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [notifOpen, setNotifOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);
  const { showToast } = useToast();
  const navigate = useNavigate();
  const handleLogout = useLogout();
  const notif = useHeaderNotifications('/dashboard');
  const unread = notif.unread;
  const workspaceName = useSelector((state) => state.manufacturer?.profile?.name) || 'Workspace';
  const loginUser = useSelector((state) => state.auth?.loginUser);
  const displayName = loginUser?.name || loginUser?.username || '';
  const displayEmail = loginUser?.email || '';
  const displayRole = extractRoleFromUser(loginUser);
  const avatarLetter = (displayName?.[0] || 'M').toUpperCase();

  const CREATE_ITEMS = [
    { label: 'Add Brand', path: '/dashboard/brands', intent: CREATE_INTENT.BRAND },
    { label: 'Add Payment Receipt', path: '/dashboard/receipts', intent: CREATE_INTENT.RECEIPT },
    { label: 'Add Debit', path: '/dashboard/wallets', intent: CREATE_INTENT.DEBIT },
    { label: 'Create Fabric Option', path: '/dashboard/fabrics', intent: CREATE_INTENT.FABRIC },
    { label: 'Create Design Request', path: '/dashboard/designs', intent: CREATE_INTENT.DESIGN },
    { label: 'Create Task', path: '/dashboard/board', intent: CREATE_INTENT.TASK },
    { label: 'Create Order', path: '/dashboard/orders', intent: CREATE_INTENT.ORDER },
  ];

  return (
    <>
      <header className="app-header bg-white border-b border-gray-100 py-3 flex items-center gap-4 sticky top-0 z-30">
        {/* Breadcrumbs / title */}
        <div className="flex-1 min-w-0">
          {breadcrumbs && breadcrumbs.length > 0 && (
            <nav className="flex items-center gap-1 text-[11px] text-gray-400 mb-0.5">
              {breadcrumbs.map((b, i) => (
                <span key={i} className="flex items-center gap-1">
                  {i > 0 && <span>/</span>}
                  <span
                    className={
                      i === breadcrumbs.length - 1
                        ? 'text-gray-500 font-medium'
                        : 'hover:text-gray-600 cursor-pointer'
                    }
                  >
                    {b.workspace ? workspaceName : b.label}
                  </span>
                </span>
              ))}
            </nav>
          )}
          <h1 className="text-[15px] font-semibold text-gray-900 truncate">{title}</h1>
        </div>

        {/* Search */}
        <div className="relative">
          {searchOpen ? (
            <input
              autoFocus
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onBlur={() => {
                setSearchOpen(false);
                setSearchQuery('');
              }}
              onKeyDown={(e) => e.key === 'Escape' && setSearchOpen(false)}
              placeholder="Search brands, orders, receipts…"
              className="w-64 text-sm px-3 py-1.5 border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:border-blue-400"
            />
          ) : (
            <button
              onClick={() => setSearchOpen(true)}
              className="flex items-center gap-2 text-sm text-gray-400 hover:text-gray-600 px-3 py-1.5 border border-gray-200 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
            >
              <span>⌕</span>
              <span className="text-[12px]">Search</span>
              <span className="text-[10px] text-gray-300 ml-1">/</span>
            </button>
          )}
        </div>

        {/* Create */}
        <div className="relative">
          <button
            onClick={() => setCreateOpen((v) => !v)}
            className="flex items-center gap-2 px-3 py-1.5 bg-[#1b3a6b] text-white text-[12px] font-medium rounded-lg hover:bg-[#152d55] transition-colors"
          >
            + Create
          </button>
          {createOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setCreateOpen(false)} />
              <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-100 rounded-lg shadow-xl z-50 py-1 overflow-hidden">
                {CREATE_ITEMS.map((item) => (
                  <button
                    key={item.label}
                    onClick={() => {
                      navigate(item.path, { state: { create: item.intent } });
                      setCreateOpen(false);
                    }}
                    className="w-full text-left px-4 py-2 text-[12px] text-gray-700 hover:bg-gray-50"
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Notifications */}
        <div className="relative">
          <button
            onClick={() => {
              if (!notifOpen) notif.load();
              setNotifOpen((v) => !v);
              setUserOpen(false);
            }}
            className="relative p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg"
          >
            <span className="text-lg">🔔</span>
            {unread > 0 && (
              <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                {unread}
              </span>
            )}
          </button>
          {notifOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setNotifOpen(false)} />
              <div className="absolute right-0 top-full mt-1 w-80 bg-white border border-gray-100 rounded-xl shadow-xl z-50 overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                  <span className="text-[13px] font-semibold text-gray-900">Notifications</span>
                  <button
                    className="text-[11px] text-blue-600 hover:underline"
                    onClick={() => {
                      navigate('/dashboard/notifications');
                      setNotifOpen(false);
                    }}
                  >
                    View all
                  </button>
                </div>
                <NotificationPreview
                  items={notif.preview}
                  loading={notif.loading}
                  unreadClass="bg-blue-50/50"
                  hoverClass="hover:bg-gray-50"
                  onOpen={(n) => {
                    notif.open(n);
                    setNotifOpen(false);
                  }}
                />
              </div>
            </>
          )}
        </div>

        {/* User menu */}
        <div className="relative">
          <button
            onClick={() => {
              setUserOpen((v) => !v);
              setNotifOpen(false);
            }}
            className="flex items-center gap-2 hover:bg-gray-50 rounded-lg px-2 py-1.5"
          >
            <div className="w-7 h-7 bg-[#1b3a6b] rounded-full flex items-center justify-center text-white text-[11px] font-semibold">
              {avatarLetter}
            </div>
            <div className="hidden sm:block text-left">
              <div className="text-[12px] font-medium text-gray-900 max-w-[140px] truncate" title={displayName}>{displayName}</div>
              <div className="text-[10px] text-gray-400 max-w-[140px] truncate">{displayRole}</div>
            </div>
          </button>
          {userOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setUserOpen(false)} />
              <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-100 rounded-lg shadow-xl z-50 py-1">
                <div className="px-4 py-3 border-b border-gray-100">
                  <div className="text-[12px] font-semibold text-gray-900 truncate" title={displayName}>{displayName}</div>
                  <div className="text-[11px] text-gray-400 truncate" title={displayEmail}>{displayEmail}</div>
                </div>
                <button
                  className="w-full text-left px-4 py-2 text-[12px] text-gray-700 hover:bg-gray-50"
                  onClick={() => {
                    navigate('/dashboard/settings');
                    setUserOpen(false);
                  }}
                >
                  Settings
                </button>
                <button
                  className="w-full text-left px-4 py-2 text-[12px] text-gray-700 hover:bg-gray-50"
                  onClick={() => {
                    showToast && showToast('Help centre coming soon', 'info');
                    setUserOpen(false);
                  }}
                >
                  Help & Support
                </button>
                <div className="border-t border-gray-100 mt-1 pt-1">
                  <button
                    className="w-full text-left px-4 py-2 text-[12px] text-red-600 hover:bg-red-50"
                    onClick={() => {
                      setUserOpen(false);
                      setLogoutModalOpen(true);
                    }}
                  >
                    Logout
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </header>

      <LogoutModal
        isOpen={logoutModalOpen}
        onClose={() => setLogoutModalOpen(false)}
        onConfirm={() => {
          setLogoutModalOpen(false);
          handleLogout();
        }}
      />
    </>
  );
}

export function Header({ title, subtitle, actions, breadcrumbs, role, ...rest }) {
  const location = useLocation();
  const loginUser = useSelector((state) => state.auth?.loginUser);
  const isBrand =
    role === 'brand' ||
    isBrandRole(loginUser) ||
    location.pathname.startsWith('/brand') ||
    (subtitle !== undefined && !breadcrumbs);

  if (isBrand) {
    return <BrandHeader title={title} subtitle={subtitle} actions={actions} {...rest} />;
  }

  return <ManufacturerHeader title={title} breadcrumbs={breadcrumbs} {...rest} />;
}

export default Header;
